import React, { useEffect, useRef, useState, useCallback } from "react";
import { T } from "../theme";

/**
 * MentionInput — a reusable textarea wrapper with @mention support.
 *
 *   <MentionInput
 *     value={caption}
 *     onChange={setCaption}
 *     friends={friendsList}
 *     placeholder="Say something..."
 *   />
 *
 * Type @ and a dropdown of friends appears (max 5, filtered live). Selecting
 * one inserts @username into the string; mentions render as neon chips via a
 * highlight layer behind the (transparent-text) textarea, so the underlying
 * value stays a plain string — safe to store and parse with parseCaption.
 *
 * Fully keyboard navigable: ArrowUp/Down, Enter/Tab select, Escape dismisses.
 * Designed to drop into any input context (captions, comments, bios).
 */


// Must match parseCaption's token charset
const MENTION_RE = /@([A-Za-z0-9_.-]+)/g;
// The partial token being typed at the caret ("@glo" → query "glo")
const ACTIVE_RE = /(^|[\s.,!?()])@([A-Za-z0-9_.-]*)$/;

/* Text metrics MUST be identical between the highlight layer and the textarea
   so chips sit exactly under the (invisible) typed characters. Chip styling is
   limited to metric-neutral properties: background, box-shadow, radius, color. */
const sharedText = {
  fontFamily: "inherit",
  fontSize: "0.88rem",
  lineHeight: 1.5,
  fontWeight: 400,
  letterSpacing: "normal",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  padding: "10px 14px",
  margin: 0,
  border: "none",
  boxSizing: "border-box",
};

function HighlightedValue({ value }) {
  if (!value) return null;
  const nodes = [];
  let last = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(value)) !== null) {
    if (m.index > last) nodes.push(value.slice(last, m.index));
    nodes.push(
      <span
        key={m.index}
        style={{
          color: T.cyan,
          background: "rgba(0,245,255,0.14)",
          boxShadow: "0 0 0 2px rgba(0,245,255,0.14), 0 0 10px rgba(0,245,255,0.15)",
          borderRadius: 5,
        }}
      >
        {m[0]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < value.length) nodes.push(value.slice(last));
  return nodes;
}

function MentionInput({
  value = "",
  onChange,
  friends = [],
  placeholder = "Say something...",
  maxLength = 280,
  rows = 2,
  style,
}) {
  const wrapRef      = useRef(null);
  const textareaRef  = useRef(null);
  const backdropRef  = useRef(null);
  const [active, setActive]       = useState(null); // { start, query } of the token being typed
  const [highlight, setHighlight] = useState(0);

  const matches = active
    ? friends
        .filter((f) => f?.username && f.username.toLowerCase().includes(active.query.toLowerCase()))
        .slice(0, 5)
    : [];
  const open = active !== null && matches.length > 0;

  // Detect the partial @token at the caret (if any)
  const refreshActive = useCallback(() => {
    const el = textareaRef.current;
    if (!el) { setActive(null); return; }
    const upToCaret = el.value.slice(0, el.selectionStart);
    const m = ACTIVE_RE.exec(upToCaret);
    if (m) {
      setActive((prev) => {
        const next = { start: upToCaret.length - m[2].length - 1, query: m[2] };
        return prev && prev.start === next.start && prev.query === next.query ? prev : next;
      });
    } else {
      setActive(null);
    }
  }, []);

  useEffect(() => { setHighlight(0); }, [active?.query, active?.start]);

  // Dismiss on click outside
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setActive(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const insertMention = (friend) => {
    const el = textareaRef.current;
    if (!el || !active) return;
    const caret = el.selectionStart;
    const inserted = `@${friend.username} `;
    const next = value.slice(0, active.start) + inserted + value.slice(caret);
    onChange(next.slice(0, maxLength));
    setActive(null);
    const newCaret = Math.min(active.start + inserted.length, maxLength);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    });
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Escape" && active) { setActive(null); e.stopPropagation(); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(matches[highlight] || matches[0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setActive(null);
    }
  };

  const syncScroll = () => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <div
        style={{
          position: "relative",
          background: T.cardSoft,
          border: T.borderSoft,
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Highlight layer — renders the same text with neon mention chips */}
        <div
          ref={backdropRef}
          aria-hidden="true"
          style={{
            ...sharedText,
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
            color: T.textPrimary,
          }}
        >
          <HighlightedValue value={value} />
          {/* trailing newline needs a placeholder char to keep heights synced */}
          {value.endsWith("\n") ? " " : null}
        </div>

        <textarea
          ref={textareaRef}
          value={value}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value); requestAnimationFrame(refreshActive); }}
          onKeyDown={handleKeyDown}
          onKeyUp={refreshActive}
          onClick={refreshActive}
          onScroll={syncScroll}
          onBlur={syncScroll}
          style={{
            ...sharedText,
            position: "relative",
            display: "block",
            width: "100%",
            resize: "vertical",
            background: "transparent",
            color: "transparent",
            caretColor: T.cyan,
            outline: "none",
          }}
        />
      </div>

      {/* Friend dropdown */}
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 40,
            background: "rgba(16,8,32,0.97)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(0,245,255,0.25)",
            borderRadius: 14,
            boxShadow: "0 12px 36px rgba(0,0,0,0.55), 0 0 20px rgba(0,245,255,0.08)",
            overflow: "hidden",
          }}
        >
          {matches.map((f, i) => (
            <div
              key={f.id || f.username}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => { e.preventDefault(); insertMention(f); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                cursor: "pointer",
                background: i === highlight ? "rgba(0,245,255,0.1)" : "transparent",
                transition: "background 0.1s",
              }}
            >
              {f.profilePic ? (
                <img
                  src={f.profilePic}
                  alt=""
                  style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: `linear-gradient(135deg, ${T.purple}, ${T.pink})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.62rem", fontWeight: 800, color: "#fff",
                  }}
                >
                  {f.username.slice(0, 2).toUpperCase()}
                </div>
              )}
              <span style={{ color: T.textPrimary, fontSize: "0.85rem", fontWeight: 600 }}>
                @{f.username}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MentionInput;
